import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberPointsHistoryDto, MemberPointsKind } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { safeFormatDate } from '../../../lib/format';
import { useAuthStore } from '../../../store/auth.store';
import { errorMessage } from './errorMessage';
import { LedgerAdjustForm } from './LedgerAdjustForm';
import { LedgerHistoryPager } from './LedgerHistoryPager';
import { formatPoints } from './ledgerTabs';

const PAGE_SIZE = 20;

/** One points ledger: 選手點數, a 限時 month or Z點. */
export function PointsLedgerTab({
  memberId,
  kind,
  period,
  balance,
  expiresAt,
  expired,
  deductOnly,
}: {
  memberId: string;
  kind: MemberPointsKind;
  period?: string;
  balance: string;
  expiresAt?: string;
  expired?: boolean;
  /** A former Z客 can only have its remaining Z點 deducted. */
  deductOnly?: boolean;
}) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const canAdjust = useAuthStore((s) => s.allowedActions?.includes('member:adjust_points'));
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');

  const { data, isError } = useQuery<MemberPointsHistoryDto>({
    queryKey: ['member-ledger', memberId, kind, period ?? null, page],
    queryFn: () =>
      api.get(`/members/${memberId}/points`, {
        params: { kind, period, page, limit: PAGE_SIZE },
      }),
  });

  const adjust = useMutation({
    mutationFn: (input: {
      operation: string;
      amount: string;
      note: string;
      operationKey?: string;
    }) =>
      api.post(`/members/${memberId}/points`, {
        kind,
        period,
        operationKey: input.operationKey,
        direction: input.operation,
        amount: input.amount,
        note: input.note,
      }),
    onSuccess: () => {
      setError('');
      setPage(1);
      void qc.invalidateQueries({ queryKey: ['member-ledger', memberId] });
      void qc.invalidateQueries({ queryKey: ['member-ledger-summary', memberId] });
    },
    onError: (failure) => setError(errorMessage(failure, t('ledgerLoadFailed'))),
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="bg-base-300 rounded-lg p-4">
          <p className="text-xs text-base-content/60 mb-1">{t('ledgerBalance')}</p>
          <p
            className={`text-2xl font-bold ${Number(balance) < 0 ? 'text-error' : 'text-primary'}`}
          >
            {formatPoints(balance)} <span className="text-sm">{t('pointsUnit')}</span>
            {Number(balance) < 0 && <span className="ml-2 text-sm">{t('ledgerOwed')}</span>}
          </p>
        </div>
        {expiresAt && (
          <div className="bg-base-300 rounded-lg p-4">
            <p className="text-xs text-base-content/60 mb-1">{t('ledgerExpiresAt')}</p>
            <p className={`text-lg font-semibold ${expired ? 'text-error' : 'text-base-content'}`}>
              {safeFormatDate(expiresAt, (d) => d.toLocaleDateString())}
              {expired && <span className="ml-2 text-sm">{t('ledgerExpired')}</span>}
            </p>
          </div>
        )}
      </div>

      {canAdjust && !expired && (
        <LedgerAdjustForm
          operations={[
            ...(deductOnly ? [] : [{ value: 'GRANT', label: t('ledgerGrant') }]),
            { value: 'DEDUCT', label: t('ledgerDeduct') },
          ]}
          amountLabel={t('ledgerAmountPoints')}
          step={kind === 'Z' ? '0.01' : '1'}
          pending={adjust.isPending}
          error={error}
          keyed={{
            scope: `${memberId}:${kind}:${period ?? ''}`,
            reload: () =>
              qc.refetchQueries({ queryKey: ['member-ledger', memberId] }, { throwOnError: true }),
          }}
          onSubmit={(input) => adjust.mutateAsync(input)}
        />
      )}
      {expired && <p className="text-sm text-base-content/60">{t('ledgerExpiredNote')}</p>}

      <div className="bg-base-200 border border-base-300 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-base-300">
            <tr>
              <th className="text-left px-4 py-2">{t('ledgerCreatedAt')}</th>
              <th className="text-left px-4 py-2">{t('ledgerType')}</th>
              <th className="text-left px-4 py-2">{t('ledgerDelta')}</th>
              <th className="text-left px-4 py-2">{t('ledgerNote')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-300">
            {data?.rows.map((row) => (
              <tr key={row.id}>
                <td className="px-4 py-2 text-base-content/60 text-xs whitespace-nowrap">
                  {safeFormatDate(row.createdAt, (d) => d.toLocaleString())}
                </td>
                <td className="px-4 py-2">{t(`ledgerSource_${row.source}`)}</td>
                <td
                  className={`px-4 py-2 font-mono font-medium ${Number(row.delta) < 0 ? 'text-error' : 'text-success'}`}
                >
                  {Number(row.delta) > 0 ? '+' : ''}
                  {formatPoints(row.delta)}
                </td>
                <td className="px-4 py-2 [overflow-wrap:anywhere]">{row.note}</td>
              </tr>
            ))}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-base-content/50">
                  {t('ledgerEmpty')}
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-error">
                  {t('ledgerLoadFailed')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {data && (
          <LedgerHistoryPager
            page={data.page}
            limit={data.limit}
            total={data.total}
            onPage={setPage}
          />
        )}
      </div>
    </div>
  );
}
